import { useEffect, useState } from 'react';
import { Bell, BellOff, BellRing, Settings } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  registerPushNotifications,
  unregisterPushNotifications,
  onForegroundMessage,
  isPushSupported,
  getPushPermission,
} from '../lib/push';
import NotificationPreferences from './NotificationPreferences';

const EnablePushButton = ({ compact = false }) => {
  const [permission, setPermission] = useState('default');
  const [working, setWorking] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return;
    setPermission(getPushPermission());
    
    // Silent registration if previously granted
    if (Notification.permission === 'granted' && !localStorage.getItem('fcm_token')) {
      registerPushNotifications({ silent: true }).catch(() => {});
    }
    
    // Foreground message handler — show as toast
    const unsub = onForegroundMessage((payload) => {
      const title = payload?.notification?.title || payload?.data?.title || 'LocalMove';
      const body = payload?.notification?.body || payload?.data?.body || '';
      toast(title, { description: body, duration: 6000 });
    });
    return unsub;
  }, []);

  const handleEnable = async () => {
    setWorking(true);
    try {
      const result = await registerPushNotifications();
      if (result.ok) {
        setPermission('granted');
        toast.success('Push notifications enabled');
      } else if (result.reason === 'denied') {
        setPermission('denied');
        toast.error('Push notifications blocked. Enable them in your browser settings.');
      } else if (result.reason === 'unsupported' || result.reason === 'not_configured') {
        toast.error("Push notifications aren't supported in this browser.");
      } else {
        toast.error('Could not enable push notifications. Please try again.');
      }
    } finally {
      setWorking(false);
    }
  };

  const handleDisable = async () => {
    setWorking(true);
    try {
      await unregisterPushNotifications();
      toast.success('Push notifications disabled on this device');
    } finally {
      setWorking(false);
    }
  };

  if (!isPushSupported()) return null;

  const enabled = permission === 'granted' && !!localStorage.getItem('fcm_token');

  if (compact) {
    return (
      <>
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={enabled ? handleDisable : handleEnable}
            disabled={working}
            className="text-zinc-500 hover:text-orange-600 px-2"
            data-testid="push-toggle-btn"
            title={enabled ? 'Disable push notifications' : 'Enable push notifications'}
          >
            {enabled ? <BellRing className="w-4 h-4 text-orange-500" /> : <BellOff className="w-4 h-4" />}
          </Button>
          {enabled && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowPrefs(true)}
              className="text-zinc-400 hover:text-zinc-700 px-1.5"
              data-testid="notif-prefs-btn"
              title="Notification preferences"
            >
              <Settings className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
        <NotificationPreferences open={showPrefs} onOpenChange={setShowPrefs} />
      </>
    );
  }

  return (
    <>
      <Button
        variant={enabled ? 'outline' : 'default'}
        size="sm"
        onClick={enabled ? handleDisable : handleEnable}
        disabled={working}
        className={enabled ? '' : 'bg-orange-500 hover:bg-orange-600 text-white'}
        data-testid="push-toggle-btn"
      >
        {enabled ? <Bell className="w-4 h-4 mr-1" /> : <BellRing className="w-4 h-4 mr-1" />}
        {enabled ? 'Notifications On' : 'Enable Push'}
      </Button>
      <NotificationPreferences open={showPrefs} onOpenChange={setShowPrefs} />
    </>
  );
};

export default EnablePushButton;
